<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="loading">
      <b-card>
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </b-card>
    </div>

    <template v-else>
      <!-- Print Button (hidden when printing) -->
      <div class="d-print-none mb-2">
        <b-button variant="primary" @click="printPnl">
          <feather-icon icon="PrinterIcon" class="mr-1" />Print P&amp;L
        </b-button>
      </div>

      <!-- Printable Content -->
      <div id="pnl-printable">
        <!-- Header Info -->
        <b-card>
          <b-row>
            <b-col md="3">
              <small class="text-muted">Flock ID</small>
              <p class="font-weight-bold">{{ pnl.flock_id || $route.params.flockId }}</p>
            </b-col>
            <b-col md="3">
              <small class="text-muted">Farm</small>
              <p class="font-weight-bold">{{ pnl.farm_name || '—' }}</p>
            </b-col>
            <b-col md="3">
              <small class="text-muted">Total Birds</small>
              <p class="font-weight-bold">{{ pnl.total_birds || '—' }}</p>
            </b-col>
            <b-col md="3">
              <small class="text-muted">Status</small>
              <p>
                <b-badge :variant="pnl.status === 'active' ? 'success' : 'secondary'">
                  {{ pnl.status || '—' }}
                </b-badge>
              </p>
            </b-col>
          </b-row>
        </b-card>

        <!-- Revenue -->
        <b-card>
          <h5 class="text-success">Revenue</h5>
          <b-table :items="revenueRows" :fields="amountFields" striped borderless small />
        </b-card>

        <!-- Costs -->
        <b-card>
          <h5 class="text-danger">Cost Breakdown</h5>
          <b-table :items="costRows" :fields="amountFields" striped borderless small />
        </b-card>

        <!-- Summary -->
        <b-card>
          <b-row>
            <b-col md="4">
              <div class="p-3 rounded text-center" :class="netProfit >= 0 ? 'bg-light-success' : 'bg-light-danger'">
                <small class="text-muted">Net Profit / Loss</small>
                <h2 :class="netProfit >= 0 ? 'text-success' : 'text-danger'">
                  ₹{{ formatNumber(netProfit) }}
                </h2>
              </div>
            </b-col>
            <b-col md="4">
              <div class="p-3 rounded text-center bg-light-primary">
                <small class="text-muted">Cost per kg Produced</small>
                <h4 class="text-primary">₹{{ formatNumber(pnl.cost_per_kg) }}</h4>
              </div>
            </b-col>
            <b-col md="4">
              <div class="p-3 rounded text-center bg-light-info">
                <small class="text-muted">Margin %</small>
                <h4 class="text-info">{{ pnl.margin_percent || '0' }}%</h4>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </div>
    </template>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import { BCard, BRow, BCol, BTable, BButton, BBadge, BSpinner } from 'bootstrap-vue'

export default {
  name: 'FlockPnl',
  components: { BCard, BRow, BCol, BTable, BButton, BBadge, BSpinner },
  data() {
    return {
      loading: false,
      pnl: {},
      amountFields: [
        { key: 'label', label: 'Item' },
        { key: 'amount', label: 'Amount (₹)', formatter: val => `₹${this.formatNumber(val)}` },
      ],
    }
  },
  computed: {
    netProfit() {
      return parseFloat(this.pnl.net_profit) || 0
    },
    revenueRows() {
      if (!this.pnl.revenue) return []
      return [{ label: 'Total Sales Amount', amount: this.pnl.revenue.total_sales || 0 }]
    },
    costRows() {
      if (!this.pnl.costs) return []
      const c = this.pnl.costs
      return [
        { label: 'Chick Cost', amount: c.chick_cost || 0 },
        { label: 'Feed Cost', amount: c.feed_cost || 0 },
        { label: 'Medicine', amount: c.medicine_cost || 0 },
        { label: 'Vaccine', amount: c.vaccine_cost || 0 },
        { label: 'Other Expenses', amount: c.other_expenses || 0 },
        { label: 'Total Costs', amount: c.total_cost || 0 },
      ]
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const { farmId, shedId, flockId } = this.$route.params
      this.loading = true
      try {
        const { data } = await axios.get(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/pnl`)
        this.pnl = data
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load P&L data', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    formatNumber(val) {
      return Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    printReport() {
      window.print()
    },
    printPnl() {
      const content = document.getElementById('pnl-printable').innerHTML
      const win = window.open('', '_blank')
      win.document.write(`
        <html><head><title>Flock P&L Report</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #f5f5f5; }
          .profit { color: green; font-weight: bold; font-size: 1.5em; }
          .loss { color: red; font-weight: bold; font-size: 1.5em; }
          h1 { color: #333; border-bottom: 2px solid #7367F0; padding-bottom: 10px; }
        </style></head>
        <body><h1>Flock P&L Report</h1>${content}</body></html>
      `)
      win.document.close()
      win.focus()
      win.print()
      win.close()
    },
  },
}
</script>

<style scoped>
.bg-light-success { background-color: rgba(40, 199, 111, 0.12); }
.bg-light-danger  { background-color: rgba(234, 84, 85, 0.12); }
.bg-light-primary { background-color: rgba(115, 103, 240, 0.12); }
.bg-light-info    { background-color: rgba(0, 207, 232, 0.12); }
</style>
